import { AnimatePresence, motion } from 'motion/react';
import type { Settings } from '@mendikot/app-core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { create } from 'zustand';
import {
  useAuthUser,
  useRemoteSettings,
  useServices,
  useSettings,
  useSettingsStore,
  useT,
} from '../app/context';
import { Icon } from '../components/ui/Icon';
import { Button } from '../components/ui/primitives';
import { isIos, isStandalone } from '../lib/device';
import { LEGAL } from '../legal';
import { recordConsent } from '../platform/privacy';

interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Captured `beforeinstallprompt` so the app can offer installation at a good moment. */
export const useInstallPrompt = create<{
  event: InstallEvent | null;
  set(e: InstallEvent | null): void;
}>((set) => ({
  event: null,
  set: (event) => set({ event }),
}));

export function captureInstallPrompt(onInstalled: () => void): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    useInstallPrompt.getState().set(e as InstallEvent);
  });
  window.addEventListener('appinstalled', () => {
    useInstallPrompt.getState().set(null);
    onInstalled();
  });
}

export function InstallButton() {
  const t = useT();
  const event = useInstallPrompt((s) => s.event);
  const { analytics } = useServices();
  if (!event) return null;
  return (
    <Button
      size="sm"
      variant="secondary"
      icon="download"
      onClick={async () => {
        analytics.log('pwa_install_prompt_shown', {});
        await event.prompt();
        await event.userChoice;
        useInstallPrompt.getState().set(null);
      }}
    >
      {t('pwa.install')}
    </Button>
  );
}

/** True until the player has confirmed they are 18+ and accepted the current privacy notice. */
export const needsWelcome = (s: Settings): boolean =>
  s.ageConfirmedAt === null || s.noticeVersion !== LEGAL.noticeVersion;

const NOTICE_ITEMS = [
  'gate.item.game',
  'gate.item.account',
  'gate.item.push',
  'gate.item.security',
  'gate.item.analytics',
] as const;

/**
 * DPDP notice and age gate, shown before anything else (and before any account exists). Both play
 * buttons confirm the player is 18+; analytics is a separate, unbundled choice either way.
 */
export function WelcomeGate() {
  const t = useT();
  const settings = useSettingsStore();
  const lang = useSettings((s) => s.lang);
  const services = useServices();
  const [underage, setUnderage] = useState(false);

  const accept = (analytics: boolean) => {
    settings.update({
      ageConfirmedAt: Date.now(),
      noticeVersion: LEGAL.noticeVersion,
      analyticsConsent: analytics ? 'granted' : 'denied',
    });
    services.applyConsent(analytics);
    services.analytics.log('consent_updated', { analytics: analytics ? 1 : 0 });
    if (!services.auth.available) return;
    void services.auth
      .ensureSignedIn()
      .then((user) => recordConsent(user.uid, analytics, lang))
      .catch((error) => services.crash.captureError(error, { where: 'consent_record' }));
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-4 pb-[calc(24px+var(--safe-bottom))] pt-[calc(24px+var(--safe-top))]">
      <h1 className="mb-4 text-center font-display text-4xl text-accent">{t('app.name')}</h1>
      <motion.section
        key={underage ? 'underage' : 'notice'}
        role="dialog"
        aria-labelledby="welcome-title"
        className="panel rounded-3xl p-5"
        initial={{ opacity: 0, transform: 'translateY(12px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      >
        {underage ? (
          <>
            <h2 id="welcome-title" className="text-lg font-bold text-ink">
              {t('gate.blockedTitle')}
            </h2>
            <p className="mt-2 text-sm text-ink-muted">{t('gate.blockedBody')}</p>
            <Button className="mt-4" block variant="secondary" onClick={() => setUnderage(false)}>
              {t('gate.blockedBack')}
            </Button>
          </>
        ) : (
          <>
            <h2 id="welcome-title" className="text-lg font-bold text-ink">
              {t('gate.title')}
            </h2>
            <p className="mt-1 font-semibold text-ink">{t('gate.age')}</p>
            <p className="mt-3 text-sm font-semibold text-ink-muted">{t('gate.noticeIntro')}</p>
            <ul className="mt-1.5 space-y-1.5 text-sm text-ink-muted">
              {NOTICE_ITEMS.map((key) => (
                <li key={key} className="flex gap-2">
                  <span
                    aria-hidden
                    className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                  />
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-ink-muted">
              {t('gate.rights')}{' '}
              <Link
                to="/privacy"
                className="font-semibold text-accent underline underline-offset-2"
              >
                {t('gate.policy')}
              </Link>
            </p>
            <div className="mt-4 grid gap-2">
              <Button block onClick={() => accept(true)}>
                {t('gate.playShare')}
              </Button>
              <Button block variant="secondary" onClick={() => accept(false)}>
                {t('gate.playNoShare')}
              </Button>
              <Button block variant="ghost" size="sm" onClick={() => setUnderage(true)}>
                {t('gate.under18')}
              </Button>
            </div>
          </>
        )}
      </motion.section>
    </div>
  );
}

/** Soft-ask for notifications after the player has enjoyed a match — never on first launch. */
export function PushPrompt() {
  const t = useT();
  const services = useServices();
  const user = useAuthUser();
  const settings = useSettingsStore();
  const prompted = useSettings((s) => s.pushPromptedAt);
  const completed = useSettings((s) => s.matchesCompleted);
  const flags = useRemoteSettings().featureFlags;
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    void services.push.supported().then(setSupported);
  }, [services.push]);

  const iosNeedsInstall = isIos() && !isStandalone();
  const eligible =
    services.online &&
    prompted === null &&
    completed >= flags.push_soft_ask_after_matches &&
    (iosNeedsInstall || (supported && services.push.permission() === 'default'));

  useEffect(() => {
    if (eligible) services.analytics.log('push_prompt_shown', {});
  }, [eligible, services.analytics]);

  return (
    <AnimatePresence>
      {eligible && (
        <motion.div
          className="panel flex items-start gap-3 rounded-3xl p-4"
          initial={{ opacity: 0, transform: 'translateY(8px)' }}
          animate={{ opacity: 1, transform: 'translateY(0px)' }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-accent-soft text-accent">
            <Icon name="bell" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-ink">{t('push.title')}</div>
            <p className="text-sm text-ink-muted">
              {iosNeedsInstall ? t('push.iosHint') : t('push.body')}
            </p>
            <div className="mt-3 flex gap-2">
              {!iosNeedsInstall && (
                <Button
                  size="sm"
                  onClick={async () => {
                    settings.update({ pushPromptedAt: Date.now() });
                    const uid = user?.uid ?? (await services.auth.ensureSignedIn()).uid;
                    const result = await services.push.enable(uid);
                    services.analytics.log('push_permission', {
                      result: result === 'unsupported' ? 'default' : result,
                    });
                    services.analytics.setUserProperties({
                      push_opt_in: result === 'granted' ? 'yes' : 'no',
                    });
                  }}
                >
                  {t('push.enable')}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => settings.update({ pushPromptedAt: Date.now() })}
              >
                {t('push.later')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
